import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';

import { useTheme } from '@mui/material';
import { DynamicFieldItemView } from 'app/shared/components/forms/FormEditor/dynamicField';
import { getStyles as getEditorStyles } from 'app/shared/components/forms/FormEditor/FormEditor.styles';
import Signature from 'app/shared/components/forms/FormEditor/signature';
import { formsTestIds } from 'app/shared/components/forms/forms.testIds';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { FieldType, FormSchemaType } from 'constants/forms';

import { getResizeHandleConfig } from './PdfCardElement.utils';

const getStyles = (theme, { isExistsInSchema, align } = {}) => {
  const { palette } = theme;

  return {
    rnd: {
      border: `1px solid ${palette.common.black}`,
    },
    emptyRnd: {
      background: isExistsInSchema ? palette.warning.light : palette.error[400],
      cursor: 'pointer !important',
      padding: 0,
    },
    dynamicField: {
      textAlign: align || 'left',
    },
    dynamicFieldLabelAlwaysVisible: {
      overflow: 'visible',
    },
  };
};

const PdfCardElement = ({
  isEmptyDynamicField,
  isExistsInSchema,
  isEditFormPath,
  index,
  isDisabledDragging,
  isEnabledResizing,
  position,
  onResizeStop,
  onDragStop,
  helloSignTag,
  field,
  editor,
  scale,
  selectedPage = 1,
  isLabelAlwaysVisible = false,
}) => {
  const theme = useTheme();
  const classes = getStyles(theme, { isExistsInSchema, align: field?.style?.align });
  const editorClasses = getEditorStyles(theme, {
    scale,
    fontSize: field?.style?.fontSize,
    backgroundColor: field?.style?.backgroundColor,
  });
  const dataDynamicStyle = field?.style ?? undefined;

  const isAspectRatioLocked = !!field && field.type === FormSchemaType.IMAGE;

  const [isDragged, setIsDragged] = useState(false);
  const contentRef = useRef(null);

  const { enableResizing, resizeHandleStyles } = useMemo(
    () =>
      getResizeHandleConfig({
        width: position.width,
        height: position.height,
        isEnabledResizing,
      }),
    [position.width, position.height, isEnabledResizing],
  );

  const onDrag = useCallback(() => setIsDragged(true), []);

  const onDragStopCallback = useCallback(
    (...props) => {
      onDragStop(...props, index);
      setTimeout(() => setIsDragged(false), 0);
    },
    [onDragStop, index],
  );

  const onResizeStopCallback = useCallback(
    (...args) => {
      onResizeStop(...args);

      const delta = args[3];

      if (delta && delta.width === 0 && delta.height === 0 && isEditFormPath) {
        const clickTarget = contentRef.current?.querySelector(
          `[data-testid="${formsTestIds.DYNAMIC_FIELD_ITEM_VIEW}"] > span`,
        );

        clickTarget?.click();
      }
    },
    [onResizeStop, isEditFormPath],
  );

  return (
    <Rnd
      bounds="parent"
      className={clsx(
        classes.rnd,
        editorClasses.dynamicField,
        'p-0',
        isEmptyDynamicField && isEditFormPath && classes.emptyRnd,
        isLabelAlwaysVisible && classes.dynamicFieldLabelAlwaysVisible,
      )}
      data-index={index}
      disableDragging={isDisabledDragging}
      enableResizing={enableResizing}
      key={index}
      lockAspectRatio={isAspectRatioLocked}
      position={position}
      resizeHandleStyles={resizeHandleStyles}
      size={position}
      onDrag={onDrag}
      onDragStop={onDragStopCallback}
      onResizeStop={onResizeStopCallback}
    >
      <div
        className={clsx(
          !!field && classes.dynamicField,
          'w-full h-full leading-none overflow-hidden',
        )}
        ref={contentRef}
        title={field?.title}
      >
        {!!helloSignTag && (
          <Signature
            data-element={FieldType.SIGNING}
            data-index={index}
            dataHellosignRequired={helloSignTag.isRequired}
            dataHellosignSigner={helloSignTag.signer}
            dataHellosignTag={helloSignTag.tag}
            dataSigningDependencegroupId={helloSignTag.dependenceGroupId}
            dataSigningDependencegroupName={helloSignTag.selectionName}
            dataSigningDisplaySignatureLine={helloSignTag.displaySignatureLine}
            dataSigningUsePoaVerbiage={helloSignTag.usePoaVerbiage}
            editor={editor}
            isNewItem={position.isNewItem}
            readOnly={!isEditFormPath || isDragged}
            rootDependenceGroupId={helloSignTag.rootDependenceGroupId}
            scale={scale}
            selectedPage={selectedPage}
          />
        )}

        {!!field && (
          <DynamicFieldItemView
            data-element={FieldType.DYNAMIC}
            data-index={index}
            dataDynamicAction={field.action}
            dataDynamicCheckboxRenderType={field.style?.checkboxRenderType}
            dataDynamicHint={field.hint}
            dataDynamicLabel={field.label}
            dataDynamicMultiplier={field.multiplier}
            dataDynamicPostfix={field.postfix}
            dataDynamicPrefix={field.prefix}
            dataDynamicStyle={dataDynamicStyle}
            dataDynamicTitle={field.title}
            dataDynamicType={field.type}
            dataDynamicUsePoaVerbiage={field.usePoaVerbiage}
            dataDynamicValue={field.value}
            dataDynamicWithLabel={field.withLabel}
            editor={editor}
            isLabelAlwaysVisible={isLabelAlwaysVisible}
            isNewItem={position.isNewItem}
            readOnly={!isEditFormPath || isDragged}
            scale={scale}
          />
        )}
      </div>
    </Rnd>
  );
};

PdfCardElement.propTypes = {
  isEmptyDynamicField: PropTypes.bool.isRequired,
  isExistsInSchema: PropTypes.bool.isRequired,
  isEditFormPath: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  isDisabledDragging: PropTypes.bool.isRequired,
  isEnabledResizing: PropTypes.bool.isRequired,
  position: PropTypes.object.isRequired,
  editor: PropTypes.object.isRequired,
  scale: PropTypes.number.isRequired,
  onResizeStop: PropTypes.func.isRequired,
  onDragStop: PropTypes.func.isRequired,
  helloSignTag: PropTypes.object,
  isLabelAlwaysVisible: PropTypes.bool,
  field: PropTypes.object,
  selectedPage: PropTypes.number,
};

export default memo(PdfCardElement);
